import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import type { AuditReportDialogProps } from '../../../../types/auditReportDialogTypes';
import { buildAuditReportMetrics } from '../utils/auditReportDialogUtils';
import AuditReportGamesTable from './AuditReportGamesTable';
import AuditReportSummarySection from './AuditReportSummarySection';

const AuditReportDialog: React.FC<AuditReportDialogProps> = React.memo(({
  open,
  onClose,
  onRevert,
  report,
  loading = false,
  reverting = false,
}) => {
  const [expandedGames, setExpandedGames] = useState<Set<string>>(new Set());
  const metrics = useMemo(() => buildAuditReportMetrics(report), [report]);

  const handleToggleGame = useCallback((gameId: string) => {
    setExpandedGames((previousValue) => {
      const nextValue = new Set(previousValue);

      if (nextValue.has(gameId)) {
        nextValue.delete(gameId);
      } else {
        nextValue.add(gameId);
      }

      return nextValue;
    });
  }, []);

  if (!report) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#EEE8FF',
          color: '#222B45',
          fontWeight: 'bold',
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '20px' }}>
          Audit Report
        </Typography>
        <Button onClick={onClose} sx={{ minWidth: 'auto', color: '#222B45' }} disabled={reverting}>
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading audit report...</Typography>
          </Box>
        ) : (
          <Box>
            <AuditReportSummarySection report={report} metrics={metrics} />
            <AuditReportGamesTable
              games={report.games || []}
              expandedGames={expandedGames}
              onToggleGame={handleToggleGame}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={reverting}
          variant="outlined"
          sx={{ color: '#2C6CA0', borderColor: '#2C6CA0', fontWeight: 600, fontSize: '15px' }}
        >
          Close
        </Button>
        <Button
          onClick={onRevert}
          variant="contained"
          disabled={reverting || loading}
          sx={{ color: '#ffffff', fontWeight: 600, fontSize: '15px', backgroundColor: '#2C6CA0' }}
        >
          {reverting ? 'Reverting...' : 'Revert Audit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

AuditReportDialog.displayName = 'AuditReportDialog';

export default AuditReportDialog;
